import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Building2,
  Code2,
  TrendingUp,
  Award,
  Heart
} from 'lucide-react'

interface PartnershipType {
  id: string
  title: string
  subtitle: string
  description: string
  icon: React.ElementType
  benefits: string[]
  idealFor: string[]
  color: string
  bgColor: string
}

export const PartnersSection: React.FC = () => {
  const { t } = useTranslation('partners')

  // Função para mapear IDs para ícones
  const getIconForPartnership = (id: string): React.ElementType => {
    const iconMap: Record<string, React.ElementType> = {
      'software-house': Code2,
      'integrador': Building2,
      'white-label': Award,
      'investidor': TrendingUp
    }
    return iconMap[id] || Code2
  }

  // Função para mapear IDs para cores
  const getColorsForPartnership = (id: string): { color: string; bgColor: string } => {
    const colorMap: Record<string, { color: string; bgColor: string }> = {
      'software-house': { color: 'text-blue-600', bgColor: 'bg-blue-50' },
      'integrador': { color: 'text-green-600', bgColor: 'bg-green-50' },
      'white-label': { color: 'text-purple-600', bgColor: 'bg-purple-50' },
      'investidor': { color: 'text-orange-600', bgColor: 'bg-orange-50' }
    }
    return colorMap[id] || { color: 'text-gray-600', bgColor: 'bg-gray-50' }
  }

  // Buscar tipos de parceria das traduções
  const partnershipTypes: PartnershipType[] = (t('partnershipTypes', { returnObjects: true }) as any[]).map((type: any) => ({
    ...type,
    icon: getIconForPartnership(type.id),
    ...getColorsForPartnership(type.id)
  }))
  return (
    <section className="py-24 bg-white">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            {t('header.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('header.description')}
          </motion.p>
        </div>

        {/* Partnership Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {partnershipTypes.map((type, index) => (
            <motion.div
              key={type.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative group"
            >
              <div className="h-full p-8 bg-white rounded-2xl border border-gray-200 hover:border-kylix-secondary/50 hover:shadow-xl transition-all">
                {/* Icon & Title */}
                <div className="flex items-start gap-4 mb-6">
                  <div className={`p-3 rounded-xl ${type.bgColor}`}>
                    <type.icon className={`w-8 h-8 ${type.color}`} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{type.title}</h3>
                    <p className="text-kylix-secondary font-medium">{type.subtitle}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 mb-6">{type.description}</p>

                {/* Benefits */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('labels.benefits')}:</h4>
                  <ul className="space-y-2">
                    {type.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-kylix-secondary rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Ideal For */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('labels.idealFor')}:</h4>
                  <div className="flex flex-wrap gap-2">
                    {type.idealFor.map((ideal) => (
                      <span key={ideal} className="px-3 py-1 bg-kylix-background text-kylix-gray rounded-full text-sm">
                        {ideal}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <button className="w-full btn-primary justify-center">
                  <Heart className="w-5 h-5" />
                  {t('labels.ctaButton')}
                </button>
              </div>
            </motion.div>
          ))}
        </div>


      </div>
    </section>
  )
}